import { useEffect } from 'react';
import NavBar from '../../components/feature/NavBar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://zjfitservices.com';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service | ZJF IT Services LLC',
      description: 'Terms of Service for ZJF IT Services LLC. Review our terms and conditions for using our IT deployment and field services.',
      url: `${siteUrl}/terms-of-service`,
      inLanguage: 'en-US',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${siteUrl}/terms-of-service` },
        ],
      },
      isPartOf: {
        '@type': 'WebSite',
        name: 'ZJF IT Services LLC',
        url: siteUrl,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title="Terms of Service | ZJF IT Services LLC — Terms & Conditions"
        description="Terms of Service for ZJF IT Services LLC. Review our terms and conditions for using our IT deployment, field services, and consulting services."
        keywords="terms of service, terms and conditions, ZJF IT Services terms, legal agreement, IT service terms"
        canonical={`${siteUrl}/terms-of-service`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <NavBar />
        
        <main className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Terms of Service</h1>
              <p className="text-zinc-400 text-sm">
                <strong>Effective Date:</strong> January 1, 2025
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
            </div>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-zinc-300 leading-relaxed">
                Welcome to <strong className="text-orange-500">ZJ Fit Services</strong>. These Terms of Service ("Terms") govern your access to and use of our website, services, and any related content. By accessing or using our website, you agree to be bound by these Terms. If you do not agree with these Terms, please do not use our website or services.
              </p>
            </section>

            {/* Acceptance of Terms */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">1. Acceptance of Terms</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                By accessing or using the ZJ Fit Services website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy. These Terms constitute a legally binding agreement between you and ZJ Fit Services.
              </p>
              <p className="text-zinc-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Any changes will be effective immediately upon posting on this page. Your continued use of our website after changes are posted constitutes your acceptance of the modified Terms.
              </p>
            </section>

            {/* Services Description */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">2. Services Description</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                ZJ Fit Services provides professional fitness facility design, consulting, and project management services, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li><strong>Facility Design:</strong> Custom gym and fitness facility layout and design services</li>
                <li><strong>Equipment Procurement:</strong> Sourcing and procurement of fitness equipment and accessories</li>
                <li><strong>Project Management:</strong> End-to-end project coordination and implementation</li>
                <li><strong>Consulting Services:</strong> Expert advice on fitness facility operations, optimization, and industry best practices</li>
                <li><strong>Partnership Opportunities:</strong> Collaboration and partnership programs for fitness industry professionals</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                The specific scope, deliverables, timeline, and pricing for each project will be outlined in a separate service agreement or proposal.
              </p>
            </section>

            {/* Use of Website */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">3. Use of Website</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-orange-500">3.1 Permitted Use</h3>
              <p className="text-zinc-300 leading-relaxed mb-4">
                You may use our website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-6">
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Use the website in any manner that could damage, disable, or impair our servers or networks</li>
                <li>Attempt to gain unauthorized access to any portion of the website or related systems</li>
                <li>Use automated systems (bots, scrapers) to access the website without our express written permission</li>
                <li>Transmit any viruses, malware, or other harmful code</li>
                <li>Engage in any activity that interferes with or disrupts the website or services</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-orange-500">3.2 Account Responsibility</h3>
              <p className="text-zinc-300 leading-relaxed">
                If you create an account or submit information through our contact or partnership forms, you are responsible for maintaining the confidentiality of your information and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.
              </p>
            </section>

            {/* Intellectual Property */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">4. Intellectual Property Rights</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                All content on the ZJ Fit Services website, including but not limited to text, graphics, logos, images, videos, software, and design elements, is the property of ZJ Fit Services or its licensors and is protected by United States and international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4">
                You may not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Copy, reproduce, distribute, or create derivative works from our content without express written permission</li>
                <li>Use our trademarks, logos, or brand elements without authorization</li>
                <li>Remove or alter any copyright, trademark, or proprietary notices</li>
                <li>Frame or mirror any portion of the website without our consent</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                Limited use of our content for personal, non-commercial purposes is permitted, provided you maintain all copyright and proprietary notices.
              </p>
            </section>

            {/* Service Agreements */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">5. Service Agreements and Contracts</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                When you engage ZJ Fit Services for a project, a separate service agreement or contract will be executed that outlines:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Scope of work and deliverables</li>
                <li>Project timeline and milestones</li>
                <li>Pricing, payment terms, and invoicing schedule</li>
                <li>Responsibilities of both parties</li>
                <li>Warranties, guarantees, and service standards</li>
                <li>Termination and cancellation policies</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                In the event of any conflict between these Terms and a separate service agreement, the terms of the service agreement shall prevail.
              </p>
            </section>

            {/* Payment Terms */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">6. Payment Terms</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                Payment terms for services will be specified in your service agreement. General payment policies include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Invoices are due within the timeframe specified in your agreement (typically 30 days)</li>
                <li>Late payments may be subject to interest charges and late fees</li>
                <li>We reserve the right to suspend services for non-payment</li>
                <li>All prices are in U.S. Dollars unless otherwise specified</li>
                <li>Refund policies will be outlined in your service agreement</li>
              </ul>
            </section>

            {/* Warranties and Disclaimers */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">7. Warranties and Disclaimers</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>Service Warranties:</strong> We strive to provide high-quality services and will make reasonable efforts to meet agreed-upon specifications and timelines. Specific warranties for services will be outlined in your service agreement.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>Website Disclaimer:</strong> Our website and its content are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the website will be uninterrupted, error-free, or secure</li>
                <li>Warranties regarding the accuracy, reliability, or completeness of content</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                We do not warrant that the website will meet your requirements or that any defects will be corrected.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">8. Limitation of Liability</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                To the fullest extent permitted by law, ZJ Fit Services, its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Loss of data or information</li>
                <li>Business interruption or downtime</li>
                <li>Cost of substitute services</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed mb-4">
                Our total liability for any claims arising from or related to our services or website shall not exceed the amount paid by you to ZJ Fit Services in the 12 months preceding the claim.
              </p>
              <p className="text-zinc-300 leading-relaxed">
                Some jurisdictions do not allow the exclusion or limitation of certain damages, so the above limitations may not apply to you.
              </p>
            </section>

            {/* Indemnification */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">9. Indemnification</h2>
              <p className="text-zinc-300 leading-relaxed">
                You agree to indemnify, defend, and hold harmless ZJ Fit Services, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from or related to your use of our website or services, your violation of these Terms, or your violation of any rights of a third party.
              </p>
            </section>

            {/* Third-Party Links */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">10. Third-Party Links and Services</h2>
              <p className="text-zinc-300 leading-relaxed">
                Our website may contain links to third-party websites or services that are not owned or controlled by ZJ Fit Services. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that we shall not be liable for any damage or loss caused by your use of any third-party content or services.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">11. Governing Law and Dispute Resolution</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of New Jersey, United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4">
                Any disputes arising from or relating to these Terms or our services shall be resolved through:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li><strong>Informal Negotiation:</strong> First, we will attempt to resolve disputes through good-faith negotiation</li>
                <li><strong>Mediation:</strong> If negotiation fails, disputes may be submitted to mediation</li>
                <li><strong>Arbitration or Litigation:</strong> If mediation is unsuccessful, disputes shall be resolved through binding arbitration or in the state or federal courts located in New Jersey</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                You agree to submit to the personal jurisdiction of these courts for the purpose of litigating any such claims.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">12. Termination</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We reserve the right to terminate or suspend your access to our website and services at any time, without prior notice or liability, for any reason, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Violation of these Terms</li>
                <li>Fraudulent, abusive, or illegal activity</li>
                <li>Non-payment of fees</li>
                <li>At our sole discretion for any other reason</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                Upon termination, your right to use the website and services will immediately cease. Provisions of these Terms that by their nature should survive termination shall survive, including intellectual property rights, disclaimers, indemnification, and limitations of liability.
              </p>
            </section>

            {/* Severability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">13. Severability</h2>
              <p className="text-zinc-300 leading-relaxed">
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>
            </section>

            {/* Entire Agreement */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">14. Entire Agreement</h2>
              <p className="text-zinc-300 leading-relaxed">
                These Terms, together with our Privacy Policy and any separate service agreements, constitute the entire agreement between you and ZJ Fit Services regarding your use of our website and services, and supersede all prior or contemporaneous communications and proposals.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">15. Contact Information</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                If you have any questions, concerns, or feedback regarding these Terms of Service, please contact us:
              </p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <p className="text-zinc-300 mb-2"><strong className="text-white">ZJ Fit Services</strong></p>
                <p className="text-zinc-300 mb-2">Email: <a href="mailto:info@zjfitservices.com" className="text-orange-500 hover:text-orange-400 underline">info@zjfitservices.com</a></p>
                <p className="text-zinc-300 mb-2">Phone: <a href="tel:+19084442222" className="text-orange-500 hover:text-orange-400 underline">(908) 444-2222</a></p>
                <p className="text-zinc-300">Location: New Jersey, United States</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <p className="text-zinc-300 leading-relaxed">
                By using the ZJ Fit Services website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree with these Terms, you must discontinue use of our website and services immediately.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}